import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { experiences, education, speakingEvents, teaching } from '../../data/experience';
import ExperienceCard from './ExperienceCard';

const CVPage: React.FC = () => (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, mb: 6, gap: 3 }}>
                <Box>
                    <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1 }}>Curriculum Vitae (CV)</Typography>
                    <Typography variant="body1" color="text.secondary">Профессиональный опыт, достижения и проекты</Typography>
                </Box>
                <Button variant="contained" startIcon={<DownloadRoundedIcon />} href="/cv.pdf" download
                        sx={{ px: 4, py: 1.5, background: 'linear-gradient(135deg, #2563EB, #7C3AED)', boxShadow: '0 4px 20px rgba(37,99,235,0.3)', whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                    Download PDF
                </Button>
            </Box>

            <Typography variant="h4" sx={{ fontSize: '1.5rem', mb: 3, fontWeight: 800 }}>💼 Опыт работы</Typography>
            {experiences.map((e, i) => <ExperienceCard key={e.id} experience={e} defaultExpanded={false} />)}

            <Typography variant="h4" sx={{ fontSize: '1.5rem', mb: 3, mt: 6, fontWeight: 800 }}>🎓 Образование</Typography>
            {education.map(edu => (
                <Card key={edu.institution} sx={{ mb: 2 }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <SchoolRoundedIcon sx={{ color: 'primary.main' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{edu.institution}</Typography>
                                </Stack>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>{edu.degree} in {edu.field}</Typography>
                                {edu.details && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{edu.details}</Typography>}
                            </Box>
                            <Chip label={edu.period} variant="outlined" sx={{ fontWeight: 500, alignSelf: { xs: 'flex-start', sm: 'center' } }} />
                        </Stack>
                    </CardContent>
                </Card>
            ))}

            <Typography variant="h4" sx={{ fontSize: '1.5rem', mb: 3, mt: 6, fontWeight: 800 }}>🎤 Конференции</Typography>
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                    {speakingEvents.map((ev, i) => (
                        <React.Fragment key={ev.title}>
                            {i > 0 && <Divider sx={{ my: 2 }} />}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                <Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CampaignRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{ev.title}</Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={2} sx={{ mt: 0.5, ml: 3.5 }}>
                                        <Typography variant="body2" color="text.secondary">{ev.conference}</Typography>
                                        <Typography variant="body2" color="text.secondary">{ev.date}</Typography>
                                    </Stack>
                                </Box>
                                {ev.link && <Link href={ev.link} target="_blank" sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}>Link <OpenInNewRoundedIcon sx={{ fontSize: 16 }} /></Link>}
                            </Box>
                        </React.Fragment>
                    ))}
                </CardContent>
            </Card>

            <Typography variant="h4" sx={{ fontSize: '1.5rem', mb: 3, mt: 6, fontWeight: 800 }}>👨‍🏫 Преподавательская деятельность</Typography>
            <Grid container spacing={2}>
                {teaching.map(c => (
                    <Grid item xs={12} sm={6} key={c.project}>
                        <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.3s' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Link href={c.link} target="_blank" sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.9rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{c.project}</Link>
                                    </Stack>
                                    {c.years && <Stack direction="row" spacing={0.5} alignItems="center"><Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{c.years}</Typography></Stack>}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">{c.description}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Container>
    </Box>
);

export default CVPage;
